import {
  Article,
  Category,
  ArticleType,
  CategoryArticles,
  ArticleResolution,
  Label,
  ArticleSortType,
  User,
} from '@wix/answers-api';
import type { NextApiRequest, NextApiResponse, Redirect } from 'next';
import { BootstrapContext } from '@wix/wix-bootstrap-api';
import { BootstrapBi } from '@wix/wix-bootstrap-bi';
import type { AspectStore } from '@wix/wix-aspects';
import { Redis } from 'ioredis';
import { ParsedUrlQuery } from 'querystring';
import { HeaderFooterProvider } from '@wix/header-footer-provider';
import { ApiGwClient } from '@wix/wix-bootstrap-api-gw-client';
import type {
  SearchRequest,
  SearchResult,
} from '@wix/ambassador-answers-search-proxy/types';

export interface GetBrandHelpcenterDataResponse {
  categories: Category[];
  categoriesTopArticles: {
    categoryId: string;
    articles: {
      id: string;
      title: string;
      uri: string;
      articleType: ArticleType;
    }[];
  }[];
  articles: Article[];
  sessionUser?: User;
}

export type ClickedItemType =
  | 'see_all'
  | 'category_selection'
  | 'sub_category_selection';

export type BootstrapContextServerNextjs = BootstrapContext & {
  apiGwClient: ApiGwClient;
} & HeaderFooterProvider & { bi: BootstrapBi.BootstrapBiLoggerFactory };

export type AppApiResponse<T = unknown> = NextApiResponse<T> & {
  locals: {
    appContext: BootstrapContextServerNextjs;
    redisClient: Redis;
  };
};

export type AppApiRequest = NextApiRequest & {
  aspects: AspectStore;
  apiGatewayClient: ApiGwClient;
};

type GetWixServerSidePropsResult = {
  props?: any;
  redirect?: Redirect;
  notFound?: true;
};

export type GetWixServerSidePropsFunction = ({
  req,
  res,
  locale,
  query,
  err,
}: {
  req: AppApiRequest;
  res: AppApiResponse;
  locale: string;
  query: ParsedUrlQuery;
  err?:
    | (Error & {
        statusCode?: number;
      })
    | null;
}) => Promise<GetWixServerSidePropsResult>;

export type HelpCenterRpcService = {
  getArticle: (dto: {
    id: string;
    locale: string;
    tenant: { id: string };
  }) => Promise<Article>;
  getArticleByUri: (dto: {
    uri: string;
    locale: string;
    withRelatedArticles: boolean;
    tenant: { id: string };
  }) => Promise<GetArticleResponse>;
  getTenantInfo: (host: string, locale: string) => Promise<any>;
  searchArticles: (dto: {
    locale: string;
    tenant: { id: string };
    text?: string;
    categoryIds?: string[];
    types?: number[];
    summarize?: boolean;
    sortType?: number;
    pageSize?: number;
    resolutions?: ArticleResolution[];
    hasAnyOfLabelIds?: string[];
    page?: number;
    ids?: string[];
  }) => Promise<ArticleSearchResult<Article>>;
  getHelpCenterData: (
    tenantId: string,
    locale: string
  ) => Promise<GetBrandHelpcenterDataResponse>;
  getCategoryByUri: (dto: {
    uri: string;
    locale: string;
    tenant: { id: string };
  }) => Promise<Category>;
  getCategoryTopArticles: (dto: {
    categoryIds: string[];
    locale: string;
    limit?: number;
    tenant: { id: string };
  }) => Promise<CategoryArticles[]>;
  validateBrandPermission: (
    tenantId: string,
    brandId: string
  ) => Promise<void | never>;
};

export type AnswersSearchProxyService = {
  search: (request: SearchRequest) => Promise<SearchResult>;
};

export type ExportRpcService = {
  getLabels: (tenantId: string) => Promise<Label[]>;
};

export type GetArticleResponse = {
  article: Article;
  relatedArticles: Article[];
};

export type ArticleSearchResultItem = {
  id: string;
  title: string;
  uri: string;
  url: string;
  description: string;
  type: ArticleType;
  categoryId: string;
  labels: Label[];
  resolution: ArticleResolution | null;
  resolutionEta?: number | null;
};

export type ArticleSearchResult<T = ArticleSearchResultItem> = {
  items: T[];
  itemsCount: number;
  searchMethod?: string;
  searchVersion?: string;
};

export type UspContent = {
  ctaDeepLink: string;
  sitePlanRequired: 'free' | 'premium' | 'business';
  suggestionDescription: string;
  timeOfUserWork: number;
  title: string;
  tooltipMainCTAText: string;
  tooltipSecondaryCTAText: string;
  userGreeting: string;
  valuePropositionGeneric: string;
};

export interface IAnswersAPI {
  getArticle: (uri: string, locale: string) => Promise<GetArticleResponse>;
  getArticleById: (id: string, locale: string) => Promise<Article>;
  getCategoryByUri: (uri: string, locale: string) => Promise<Category>;
  getArticles: (categoryIds: string[], locale: string) => Promise<Article[]>;
  searchArticles: (
    locale: string,
    filter: ArticleFilter
  ) => Promise<ArticleSearchResult>;
  getHelpCenterData: (locale: string) => Promise<HelpCenterData>;
  getCategoryTopArticles: (
    categoryIds: string[],
    locale: string,
    limit?: number
  ) => Promise<CategoryArticles[]>;
  getPageSeo: (locale: string, pageType: PageType) => Promise<PageSeo>;
  validateBrandPermission: (
    tenantId: string,
    brandId: string
  ) => Promise<void | never>;
  getTenantInfo: (host: string, locale: string) => Promise<any>;
  getLabels: () => Promise<Label[]>;
}

export type ArticleFilter = {
  text: string;
  resolutions?: ArticleResolution[];
  pageSize?: number;
  page?: number;
  hasAnyOfLabelIds?: string[];
  sortType?: ArticleSortType;
  fromResolutionEta?: string;
  toResolutionEta?: string;
  hasResolutionEta?: boolean;
  ids?: string[];
};

export enum PageType {
  Article,
  Homepage,
  Category,
  SearchResults,
  Ticket,
  RoadMap,
  Profile,
  Contact,
}

type Robots = {
  noArchive: boolean;
  noFollow: boolean;
  noIndex: boolean;
};

export type PageSeo = {
  description: string;
  locale: string;
  title: string;
  robots: Robots;
  imageUrl: string;
};

export enum HttpStatusCode {
  OK = 200,
  MOVED_PERMANENTLY = 301,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
}

export enum UploadType {
  IMAGE = 'image',
  FILE = 'file',
}

export const ALL_RESOLUTIONS = [
  ArticleResolution.PLANNED,
  ArticleResolution.IN_DEVELOPMENT,
  ArticleResolution.COMING_SOON,
  ArticleResolution.RELEASED,
];

export enum RoadmapFilterType {
  ALL = 10,
  FOLLOWING = 20,
  ETA_THIS_QUARTER = 30,
  ETA_NEXT_QUARTER = 40,
}

export type RoadmapFeatureResolution = ArticleResolution | 'all';
export interface CustomWindow extends Window {
  wixRecorder: { addLabel: (label: string) => void };
  TB_LOGS: {
    onReady: (func: () => void) => void;
    setUser: (data: Record<string, string | undefined>) => void;
  };
}

export type HelpCenterData = {
  articles: Article[];
  categories: Category[];
  categoriesTopArticles: CategoryArticles[];
};
